import { useQuery } from "@tanstack/react-query";
import { AlertCircle, Loader2, Radar, TrendingDown, TrendingUp, Trash2 } from "lucide-react";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { label, SKU } from "@/entities/pricing/model/priceBook";
import { usePriceBook } from "@/entities/pricing/model/usePriceBook";
import { Badge } from "@/shared/ui/badge";
import { Button } from "@/shared/ui/button";
import { Card, CardContent } from "@/shared/ui/card";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/shared/ui/dialog";
import { QueryErrorBanner } from "@/shared/ui/QueryErrorBanner";

import { geoService, type GeoRunSummary } from "../api/geoService";
import { GEO_CONTENT_PLAN_TEXT, GEO_ETA_TEXT, GEO_PROBE_SCOPE_TEXT } from "../model/geoCopy";
import { TrendLine } from "./components/report/TrendLine";
import { errorText } from "@/shared/lib/apiErrors";
import { formatScoreDelta, roundDelta } from "../lib/scoreDelta";

const ACTIVE = new Set(["RUNNING"]);

/** 把「逗号/顿号/换行」分隔的竞品字符串解析成去空数组。 */
function parseCompetitors(raw: string): string[] {
  return raw
    .split(/[,，、\n]/)
    .map((s) => s.trim())
    .filter(Boolean);
}

/** ISO 时间 → "YYYY-MM-DD HH:mm"(与 GeoReportDetailPage 的 fmtTime 同口径)。 */
function fmtDateTime(iso: string | null | undefined): string {
  if (!iso) return "";
  const m = iso.match(/^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2})/);
  return m ? `${m[1]} ${m[2]}` : iso;
}

/** ISO 时间 → "MM-DD"(与 TrendLine 横轴同口径),用于 hero 概要卡的短日期。 */
function fmtDateShort(iso: string | null | undefined): string {
  return (iso ?? "").slice(5, 10);
}

/** 评级 → 色调 token(与 report/ScoreHero 的 优/良/一般 语义对齐,但走 success/info/warning/destructive 语义 token,不直写 hex)。
 *  评级缺席(尚无评级数据)给中性 muted 调,不与「差评级」的 destructive 混淆。 */
function gradeTone(grade: string | null | undefined): {
  block: string;
  text: string;
  chip: string;
} {
  if (grade === "优")
    return { block: "bg-success/10", text: "text-success", chip: "bg-success/15 text-success" };
  if (grade === "良")
    return { block: "bg-info/10", text: "text-info", chip: "bg-info/15 text-info" };
  if (grade === "一般")
    return { block: "bg-warning/10", text: "text-warning", chip: "bg-warning/15 text-warning" };
  if (grade) {
    return {
      block: "bg-destructive/10",
      text: "text-destructive",
      chip: "bg-destructive/15 text-destructive",
    };
  }
  return { block: "bg-muted", text: "text-foreground", chip: "bg-muted text-muted-foreground" };
}

/** 按（租户内)时间顺序算「较上轮」delta:只在两轮都有总分时给,拿不到就不显示(禁止编数)。 */
export function computeDeltaById(runs: GeoRunSummary[]): Map<number, number> {
  const succ = runs.filter((r) => r.status === "SUCCESS" && r.overallScore != null);
  const map = new Map<number, number>();
  succ.forEach((r, i) => {
    const prev = succ[i + 1];
    if (prev?.overallScore != null && r.overallScore != null) {
      // 1006:浮点差(+1.3000000000000007)统一保留 1 位小数,与报告页 ScoreHero 同口径
      map.set(r.id, roundDelta(r.overallScore - prev.overallScore));
    }
  });
  return map;
}

function StatusBadge({ status }: { status: string }) {
  if (status === "SUCCESS")
    return (
      <Badge className="border-transparent bg-success text-success-foreground hover:bg-success/90">
        完成
      </Badge>
    );
  if (status === "FAILED") return <Badge variant="destructive">失败</Badge>;
  return (
    <Badge className="border-transparent bg-primary/15 text-primary hover:bg-primary/20">
      体检中
    </Badge>
  );
}

const HOW_CARDS: Array<{ dot: string; title: string; desc: string }> = [
  {
    dot: "bg-success",
    title: "联网实测",
    desc: "20 个真实问题走联网检索现场作答,逐条留证据(回答原文、引用链接、模型与采集时间)。",
  },
  {
    dot: "bg-warning",
    title: "生态推演 · 4 平台",
    desc: "文心/Kimi/元宝/纳米 按各自内容生态由实测结果推演,重跑结果一致,报告内逐处标注。",
  },
  {
    dot: "bg-primary",
    title: "信任核验 + 内容计划",
    desc: `顺带核验 400 电话/官网备案/百科三资产,并产出 ${GEO_CONTENT_PLAN_TEXT}内容投放计划。`,
  },
];

/** 单条体检记录卡:大分数块 + 评级 + 日期 + delta(能算出才显) + 状态 + 查看报告/删除。 */
function GeoRunRow({
  run,
  delta,
  deleting,
  onView,
  onDeleteClick,
}: {
  run: GeoRunSummary;
  delta: number | null;
  deleting: boolean;
  onView: () => void;
  onDeleteClick: () => void;
}) {
  const isRunning = ACTIVE.has(run.status);
  const isFailed = run.status === "FAILED";
  const tone = gradeTone(run.grade);

  return (
    <Card className={isRunning ? "border-primary/40" : undefined}>
      <CardContent className="flex flex-wrap items-center gap-4 p-5">
        <button
          type="button"
          onClick={onView}
          className="flex min-w-0 flex-1 items-center gap-4 rounded-lg text-left"
        >
          <div
            className={`flex h-16 w-16 shrink-0 flex-col items-center justify-center gap-0.5 rounded-xl ${
              isRunning ? "bg-primary/10" : isFailed ? "bg-destructive/10" : tone.block
            }`}
          >
            {isRunning ? (
              <Loader2 className="h-5 w-5 animate-spin text-primary" />
            ) : isFailed ? (
              <AlertCircle className="h-5 w-5 text-destructive" />
            ) : (
              <>
                <span className={`text-xl font-extrabold tabular-nums ${tone.text}`}>
                  {run.overallScore ?? "—"}
                </span>
                {run.grade && (
                  <span className={`text-2xs font-semibold ${tone.text}`}>{run.grade}</span>
                )}
              </>
            )}
          </div>

          <div className="min-w-0 flex-1 space-y-1">
            <div className="flex flex-wrap items-center gap-2">
              <span className="truncate text-sm font-semibold">{run.brand}</span>
              {run.baseline && (
                <Badge variant="outline" data-testid="geo-run-baseline-tag">
                  基线
                </Badge>
              )}
              <StatusBadge status={run.status} />
              {delta != null && delta !== 0 && (
                <span
                  data-testid="geo-run-delta"
                  className={`inline-flex items-center gap-0.5 text-xs font-semibold ${
                    delta > 0 ? "text-success" : "text-destructive"
                  }`}
                >
                  {delta > 0 ? (
                    <TrendingUp className="h-3 w-3" />
                  ) : (
                    <TrendingDown className="h-3 w-3" />
                  )}
                  {delta > 0 ? `+${delta}` : delta}
                </span>
              )}
              {run.createdAt && (
                <span className="text-xs text-muted-foreground">{fmtDateTime(run.createdAt)}</span>
              )}
            </div>
            {isRunning && (
              <p className="text-xs text-muted-foreground">
                AI 正在联网实测与生态推演,完成后自动出报告(预计{GEO_ETA_TEXT})
              </p>
            )}
            {isFailed && <p className="text-xs text-destructive">本轮体检失败,可发起新一轮重试</p>}
          </div>
        </button>

        {!isRunning && (
          <Button size="sm" onClick={onView}>
            查看报告
          </Button>
        )}
        {/* 删除:不可逆 → 二次确认(追爆舱那批 codex 抓过「花钱/不可逆动作无二次确认」)。
            基线轮后端会拒并回人话 message,这里不预先灰掉按钮 —— 让用户看见「为什么不能删」
            比一个说不出理由的灰按钮强。 */}
        <button
          aria-label={`删除 ${run.brand} 这一轮`}
          data-testid="geo-run-delete"
          disabled={deleting}
          onClick={onDeleteClick}
          className="shrink-0 rounded p-2 text-muted-foreground transition-colors hover:text-destructive disabled:opacity-40"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </CardContent>
    </Card>
  );
}

/** GEO雷达发起页(2026-09-07 重排照 RunList mockup):深墨蓝 hero 主张带 + 左发起表单 / 右体检记录两栏。 */
export default function GeoRadarPage() {
  const navigate = useNavigate();
  const [brandHint, setBrandHint] = useState("");
  const [industry, setIndustry] = useState("");
  const [competitors, setCompetitors] = useState("");
  const [benchmark, setBenchmark] = useState("");
  // S2-T6 企业画像扩展三字段(全选填;只影响新建矩阵那一轮的问法与受众表回显)
  const [coreProducts, setCoreProducts] = useState("");
  const [targetAudience, setTargetAudience] = useState("");
  const [expansionDirection, setExpansionDirection] = useState("");
  const [starting, setStarting] = useState(false);
  const [startError, setStartError] = useState<string | null>(null);
  // 删除:不可逆 → 二次确认。pendingDelete 非空即弹确认框;deleteError 直接回显后端人话
  // (基线轮/在途轮拒删各有各的理由,别糊成一句「删除失败」)。
  const [pendingDelete, setPendingDelete] = useState<GeoRunSummary | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  // 价签来自后端价目表(0831 起 geo.probe 是 LIST 固定价);取不到价不编数字,渲染骨架口径
  const { book } = usePriceBook();
  const geoCredits = book.unit(SKU.geoProbe, "default");

  const runsQuery = useQuery({
    queryKey: ["geo", "runs"],
    queryFn: () => geoService.listRuns(20),
    refetchInterval: (q) => ((q.state.data ?? []).some((r) => ACTIVE.has(r.status)) ? 3000 : false),
    meta: { suppressGlobalErrorToast: true },
  });

  // useMemo 稳定引用:data 是 undefined 时 `?? []` 每次渲染都会新建数组,
  // 会让下面依赖 runs 的两个 useMemo 形同虚设(eslint react-hooks/exhaustive-deps 会报)。
  const runs = useMemo(() => runsQuery.data ?? [], [runsQuery.data]);
  // 品牌名称 2026-09-07 起必填(GEO 雷达重排拍板):按钮 disabled + 提示,而不是静默用产品树兜底。
  const brandMissing = brandHint.trim().length === 0;
  const deltaById = useMemo(() => computeDeltaById(runs), [runs]);
  const latestSuccess = useMemo(
    () => runs.find((r) => r.status === "SUCCESS" && r.overallScore != null) ?? null,
    [runs]
  );

  const start = async () => {
    if (brandMissing) return; // 按钮已 disabled,这里是双保险
    setStarting(true);
    setStartError(null);
    try {
      const res = await geoService.createRun({
        brandHint: brandHint.trim(),
        industry: industry.trim(),
        competitors: parseCompetitors(competitors),
        benchmark: benchmark.trim(),
        coreProducts: coreProducts.trim(),
        targetAudience: targetAudience.trim(),
        expansionDirection: expansionDirection.trim(),
      });
      // E0:发起后直接进详情页(RUNNING 态在详情页轮询到出报告)
      navigate(`/geo-radar/runs/${res.id}`);
    } catch (e: unknown) {
      setStartError(errorText(e));
    } finally {
      setStarting(false);
    }
  };

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    const id = pendingDelete.id;
    setDeletingId(id);
    setDeleteError(null);
    try {
      await geoService.deleteRun(id);
      setPendingDelete(null);
      await runsQuery.refetch();
    } catch (e: unknown) {
      // 后端对「基线轮/在途轮」返 CONFLICT + 人话 message,原样回显 —— 用户得知道为什么不能删
      setDeleteError(errorText(e));
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2">
        <Radar className="h-6 w-6 text-primary" />
        <h1 className="text-2xl font-semibold">GEO雷达</h1>
        <span className="text-sm text-muted-foreground">
          你的品牌在各家 AI 嘴里是什么样 —— 体检、对比、再给出扭转它的内容计划
        </span>
      </div>

      {/* ═══ Hero:深墨蓝品牌主张带(视觉唯一依据 = RunList mockup,允许字面 hsl 不走皮肤 token) ═══ */}
      <div className="relative overflow-hidden rounded-2xl bg-[hsl(213_70%_12%)] px-6 py-8 text-white sm:px-10 sm:py-9">
        <div className="relative flex flex-col gap-8 lg:flex-row lg:items-start lg:justify-between">
          <div className="flex max-w-xl flex-col gap-3">
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-[hsl(188_95%_60%)]">
              <Radar className="h-3.5 w-3.5" />
              <span>GEO RADAR · AI 可见度监测</span>
            </div>
            <h2 className="text-2xl font-bold leading-tight sm:text-[28px]">
              让 AI 主动推荐你的品牌
            </h2>
            <p className="text-sm leading-relaxed text-white/70">
              {GEO_PROBE_SCOPE_TEXT},20
              个买家真实问题现场作答。逐条留证:回答原文、引用来源、采集时间。
              <br />
              问题集只根据您填写的品牌与企业画像生成,不读取其他数据。
            </p>
          </div>

          {/* 最新体检概要卡 */}
          <div className="flex w-full flex-shrink-0 flex-col gap-3 sm:w-[380px]">
            <div
              className="rounded-xl border border-white/10 bg-white/5 p-6"
              data-testid="geo-hero-summary"
            >
              {latestSuccess ? (
                <div className="flex flex-col gap-3">
                  <div className="flex items-center justify-between gap-2 text-xs text-white/60">
                    <span className="truncate">
                      最新体检 · {latestSuccess.brand}
                      {latestSuccess.createdAt ? ` · ${fmtDateShort(latestSuccess.createdAt)}` : ""}
                    </span>
                    {latestSuccess.grade && (
                      <span
                        className={`shrink-0 rounded-full px-2.5 py-0.5 text-2xs font-semibold ${
                          gradeTone(latestSuccess.grade).chip
                        }`}
                      >
                        {latestSuccess.grade}
                      </span>
                    )}
                  </div>
                  <div className="flex items-end gap-3">
                    <div className="text-6xl font-extrabold leading-none tabular-nums">
                      {latestSuccess.overallScore}
                    </div>
                    <div className="flex flex-col gap-1 pb-1.5">
                      {(() => {
                        const d = deltaById.get(latestSuccess.id);
                        if (d == null || d === 0) return null;
                        return (
                          <span
                            data-testid="geo-hero-delta"
                            className={`inline-flex items-center gap-1 text-sm font-semibold ${
                              d > 0 ? "text-success" : "text-destructive"
                            }`}
                          >
                            {d > 0 ? (
                              <TrendingUp className="h-3.5 w-3.5" />
                            ) : (
                              <TrendingDown className="h-3.5 w-3.5" />
                            )}
                            {formatScoreDelta(d)} 较上轮
                          </span>
                        );
                      })()}
                      <span className="text-2xs text-white/50">AI 综合可见度 / 100</span>
                    </div>
                  </div>
                </div>
              ) : (
                <div
                  className="flex flex-col items-center gap-2 py-6 text-center"
                  data-testid="geo-hero-empty"
                >
                  <Radar className="h-6 w-6 text-white/35" />
                  <p className="text-xs leading-relaxed text-white/60">
                    还没有完成的体检轮次。发起第一轮体检,看看 AI 现在怎么向买家介绍你的品牌。
                  </p>
                </div>
              )}
            </div>
            {/* 近几轮总分趋势:复用现有 TrendLine(数据<2轮时组件自身返回 null,不额外处理)。 */}
            {latestSuccess && <TrendLine runs={runs} />}
          </div>
        </div>
      </div>

      {/* ═══ 主体两栏:左发起表单 / 右体检记录 ═══ */}
      <div className="grid gap-4 lg:grid-cols-[380px_minmax(0,1fr)] lg:items-start">
        {/* 左:发起表单 */}
        <Card>
          <CardContent className="space-y-4 p-6">
            <div className="space-y-1">
              <div className="text-base font-bold">发起体检</div>
              <p className="text-2xs text-muted-foreground">
                以下内容决定 AI 会被问什么 —— 填得越准,报告越贴近真实买家
              </p>
            </div>

            <div className="space-y-1.5">
              <label htmlFor="geo-brand" className="text-sm font-medium">
                品牌名称 <span className="text-destructive">*</span>
              </label>
              <input
                id="geo-brand"
                data-testid="geo-brand"
                className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                placeholder="如:云帆智造"
                value={brandHint}
                onChange={(e) => setBrandHint(e.target.value)}
              />
              {brandMissing && (
                <p className="text-2xs text-muted-foreground" data-testid="geo-brand-hint">
                  必填 —— AI 需要知道要体检哪个品牌
                </p>
              )}
            </div>

            <div className="space-y-1.5">
              <label htmlFor="geo-industry" className="text-sm font-medium">
                所在行业
              </label>
              <input
                id="geo-industry"
                data-testid="geo-industry"
                className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                placeholder="如:商标转让"
                value={industry}
                onChange={(e) => setIndustry(e.target.value)}
              />
            </div>

            <div className="space-y-1.5">
              <label htmlFor="geo-competitors" className="text-sm font-medium">
                竞品品牌{" "}
                <span className="font-normal text-muted-foreground">(逗号分隔,可多个)</span>
              </label>
              <input
                id="geo-competitors"
                data-testid="geo-competitors"
                className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                placeholder="如:权大师,猪八戒"
                value={competitors}
                onChange={(e) => setCompetitors(e.target.value)}
              />
            </div>

            <div className="space-y-1.5">
              <label htmlFor="geo-benchmark" className="text-sm font-medium">
                行业标杆 <span className="font-normal text-muted-foreground">(选填,用于对标)</span>
              </label>
              <input
                id="geo-benchmark"
                data-testid="geo-benchmark"
                className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                placeholder="如:行业头部品牌"
                value={benchmark}
                onChange={(e) => setBenchmark(e.target.value)}
              />
            </div>

            <details data-testid="geo-advanced" className="group rounded-md border bg-muted/30">
              <summary className="cursor-pointer select-none px-3 py-2 text-sm font-medium text-muted-foreground">
                企业画像 <span className="font-normal">(选填,影响问法倾向)</span>
              </summary>
              <div className="space-y-3 px-3 pb-3">
                <div className="space-y-1">
                  <label htmlFor="geo-core-products" className="text-xs text-muted-foreground">
                    核心产品/服务(问题矩阵向其倾斜)
                  </label>
                  <input
                    id="geo-core-products"
                    data-testid="geo-core-products"
                    className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                    placeholder="如:商标转让、专利代理"
                    value={coreProducts}
                    onChange={(e) => setCoreProducts(e.target.value)}
                  />
                </div>
                <div className="space-y-1">
                  <label htmlFor="geo-target-audience" className="text-xs text-muted-foreground">
                    目标受众(问法贴合其典型顾虑)
                  </label>
                  <input
                    id="geo-target-audience"
                    data-testid="geo-target-audience"
                    className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                    placeholder="如:中小企业主、创业者"
                    value={targetAudience}
                    onChange={(e) => setTargetAudience(e.target.value)}
                  />
                </div>
                <div className="space-y-1">
                  <label
                    htmlFor="geo-expansion-direction"
                    className="text-xs text-muted-foreground"
                  >
                    扩张方向(纳入选型类问法)
                  </label>
                  <input
                    id="geo-expansion-direction"
                    data-testid="geo-expansion-direction"
                    className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                    placeholder="如:华南市场、涉外知产"
                    value={expansionDirection}
                    onChange={(e) => setExpansionDirection(e.target.value)}
                  />
                </div>
              </div>
              <p className="px-3 pb-3 text-2xs text-muted-foreground">
                画像只影响新建问题矩阵的那一轮;已有基线的后续轮仍复用基线问题(同口径可对比)。
              </p>
            </details>

            {startError && (
              <div className="flex items-center gap-2 rounded-md bg-destructive/10 px-3 py-2 text-xs text-destructive">
                <AlertCircle className="h-3.5 w-3.5 shrink-0" /> {startError}
              </div>
            )}

            <Button
              data-testid="geo-start"
              className="w-full"
              onClick={() => void start()}
              disabled={starting || brandMissing}
            >
              {starting ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Radar className="mr-2 h-4 w-4" />
              )}
              开始体检
              {label(geoCredits, (c) => ` · ${c} 积分`)}
            </Button>

            <div
              className="rounded-lg border bg-muted/30 px-3 py-2.5 text-xs"
              data-testid="geo-cost-line"
            >
              预计 <b>{GEO_ETA_TEXT}</b> 出报告{label(geoCredits, (c) => ` · ${c} 积分 / 次`)}
            </div>

            <p className="text-center text-2xs text-muted-foreground">
              仅使用本表单内容生成问题集,不读取知识库
            </p>
          </CardContent>
        </Card>

        {/* 右:体检记录 */}
        <div className="space-y-3">
          <div className="flex items-baseline justify-between px-1">
            <div className="text-base font-bold">体检记录</div>
            <span className="text-xs text-muted-foreground">
              {runs.length > 0
                ? `共 ${runs.length} 轮 · 每轮独立留证可回溯`
                : "首轮自动设为基线,可在报告页手动改指"}
            </span>
          </div>

          {/* audit-0713 C1b:加载/错误不再伪装成「暂无记录」 */}
          {runsQuery.isLoading && <div className="text-sm text-muted-foreground">加载中…</div>}
          <QueryErrorBanner isError={runsQuery.isError} onRetry={() => void runsQuery.refetch()} />
          {!runsQuery.isLoading && !runsQuery.isError && runs.length === 0 && (
            <Card>
              <CardContent className="p-10 text-center text-sm text-muted-foreground">
                暂无记录,先在左侧发起第一轮体检
              </CardContent>
            </Card>
          )}

          <div className="space-y-3">
            {runs.map((r) => (
              <GeoRunRow
                key={r.id}
                run={r}
                delta={deltaById.get(r.id) ?? null}
                deleting={deletingId === r.id}
                onView={() => navigate(`/geo-radar/runs/${r.id}`)}
                onDeleteClick={() => setPendingDelete(r)}
              />
            ))}
          </div>

          {/* 怎么做的 · 三步说明(收成 mockup 底部那条窄带) */}
          <div className="grid gap-3 pt-1 sm:grid-cols-3" data-testid="geo-how-cards">
            {HOW_CARDS.map((c) => (
              <div key={c.title} className="rounded-xl border bg-card px-3.5 py-3">
                <div className="mb-1 flex items-center gap-2 text-xs font-bold">
                  <span className={`h-2 w-2 rounded-full ${c.dot}`} />
                  {c.title}
                </div>
                <p className="text-2xs leading-relaxed text-muted-foreground">{c.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* 删除二次确认:不可逆动作不做「点一下就没了」。基线轮/在途轮的拒绝理由在这里回显。 */}
      <Dialog
        open={!!pendingDelete}
        onOpenChange={(v) => {
          if (deletingId !== null) return; // 删除中不许关,免得用户以为没删成又点一遍
          if (!v) {
            setPendingDelete(null);
            setDeleteError(null);
          }
        }}
      >
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>删除这一轮监测?</DialogTitle>
          </DialogHeader>
          <div className="space-y-2 text-sm">
            <p>
              <b>{pendingDelete?.brand}</b>
              {pendingDelete?.overallScore != null && (
                <span className="ml-1 text-muted-foreground">
                  · 综合分 {pendingDelete.overallScore}
                </span>
              )}
            </p>
            <p className="text-xs text-muted-foreground">
              删除后这轮报告不再出现在列表与趋势图里。已消耗的积分不退。
            </p>
            {deleteError && (
              <p
                className="rounded-md bg-destructive/10 px-2.5 py-2 text-xs text-destructive"
                data-testid="geo-delete-error"
              >
                {deleteError}
              </p>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              disabled={deletingId !== null}
              onClick={() => {
                setPendingDelete(null);
                setDeleteError(null);
              }}
            >
              取消
            </Button>
            <Button
              variant="destructive"
              disabled={deletingId !== null}
              onClick={() => void confirmDelete()}
              data-testid="geo-delete-confirm"
            >
              {deletingId !== null && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
              确认删除
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
